import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { masterclassApi, errorMessage } from './api';
import { MasterclassLayout, MasterclassError } from './MasterclassLayout';
import { ClinicalStepper, ClinicalStep } from './ClinicalStep';
import { MasterclassRPGIntro } from './MasterclassRPGIntro';
import { MasterclassRPGFinal } from './MasterclassRPGFinal';
import { useMasterclassVisuals } from './useMasterclassVisuals';

export default function MasterclassPlayer() {
  const { classId } = useParams();
  const { catalog: visualCatalog } = useMasterclassVisuals(classId);
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const lock = useRef(false);
  const start = useCallback(async (restart = false) => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError('');
    try { const { data } = await masterclassApi.post(`/masterclasses/${classId}/sessions`, { restart }); setState(data); }
    catch (err) { setError(errorMessage(err)); }
    finally { lock.current = false; setBusy(false); }
  }, [classId]);
  useEffect(() => { setState(null); start(); }, [start]);

  const act = async (action, option) => {
    if (lock.current || !state?.question) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const { data } = await masterclassApi.post(`/masterclasses/sessions/${state.session_id}/${action}`, { id_paso: state.question.id_paso, ...(option ? { option } : {}) });
      setState(data);
      if (action === 'next') window.scrollTo({ top: 0, behavior: 'instant' });
    } catch (err) {
      setError(errorMessage(err));
      // Reconcile a response lost in transit without submitting a second guess.
      try { const { data } = await masterclassApi.get(`/masterclasses/sessions/${state.session_id}`); setState(data); } catch (_) { /* Keep the last confirmed step and offer retry. */ }
    } finally { lock.current = false; setBusy(false); }
  };

  const sessionAction = async (action) => {
    if (lock.current || !state) return;
    lock.current = true; setBusy(true); setError('');
    try {
      const { data } = await masterclassApi.post(`/masterclasses/sessions/${state.session_id}/${action}`);
      if (action === 'record') navigate('/');
      else { setState(data); window.scrollTo({ top: 0 }); }
    } catch (err) { setError(errorMessage(err)); }
    finally { lock.current = false; setBusy(false); }
  };
  const backTo = state ? `/masterclasses/modules/${encodeURIComponent(state.module)}${state.submodule !== 'General' ? `/${encodeURIComponent(state.submodule)}` : ''}` : '/masterclasses';

  return (
    <MasterclassLayout backTo={backTo} backLabel="Masterclasses">
      {error && <MasterclassError message={error} onRetry={() => start()} />}
      {!state && busy && <p data-testid="clinical-loading" role="status" className="flex items-center gap-3 py-12 text-sm"><Loader2 className="animate-spin" size={20} />Cargando tu masterclass…</p>}
      {state && <>
        <p data-testid="clinical-specialty" className="mb-3 text-xs font-bold text-neutral-500">{state.specialty}</p>
        <h1 data-testid="clinical-class-title" className="mc-title mb-8">{state.title}</h1>
        {state.status === 'completed' ? <MasterclassRPGFinal state={state} busy={busy} onRecord={() => sessionAction('record')} onRestart={() => start(true)} /> : !state.briefing_seen ? <MasterclassRPGIntro state={state} busy={busy} onBegin={() => sessionAction('begin')} /> : <><ClinicalStepper state={state} /><ClinicalStep state={state} busy={busy} visual={visualCatalog?.items.find(item => item.steps.includes(state.question.id_paso))} onAnswer={(option) => act('answer', option)} onNext={() => act('next')} /></>}
      </>}
    </MasterclassLayout>
  );
}